import { useRef, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { useGameStore } from '@stores/gameStore';

interface GameCameraProps {
  enableOrbitControls?: boolean;
}

/**
 * Dynamic game camera that follows the plane
 * Adjusts position based on game phase and multiplier
 */
export function GameCamera({ enableOrbitControls = false }: GameCameraProps): React.JSX.Element {
  const { camera } = useThree();
  const { multiplier, gamePhase } = useGameStore();
  const targetPosition = useRef(new THREE.Vector3(0, 10, 25));
  const targetLookAt = useRef(new THREE.Vector3(0, 5, 0));

  useEffect(() => {
    // Set initial camera position
    camera.position.set(0, 10, 25);
    camera.lookAt(0, 5, 0);
  }, [camera]);

  useFrame(() => {
    if (enableOrbitControls) return;

    // Calculate camera target based on game state
    if (gamePhase === 'betting' || gamePhase === 'waiting') {
      // Overview position showing runway
      targetPosition.current.set(-5, 8, 20);
      targetLookAt.current.set(-5, 3, 0);
    } else if (gamePhase === 'playing') {
      // Follow the plane as it climbs
      const progress = Math.min((multiplier - 1) / 9, 1);
      const planeX = -10 + progress * 25;
      const planeY = 2 + progress * 15;

      // Camera follows behind and slightly above
      targetPosition.current.set(
        planeX - 10,
        planeY + 5,
        18 - progress * 5 // Come closer as plane climbs
      );
      targetLookAt.current.set(planeX + 2, planeY, 0);
    } else if (gamePhase === 'crashed') {
      // Pull back for crash view
      targetPosition.current.set(0, 15, 30);
      targetLookAt.current.set(0, 5, 0);
    }

    // Smooth camera movement
    camera.position.lerp(targetPosition.current, 0.03);

    // Smooth look-at
    const currentLookAt = new THREE.Vector3();
    camera.getWorldDirection(currentLookAt);
    currentLookAt.multiplyScalar(-50).add(camera.position);

    const newLookAt = new THREE.Vector3().lerpVectors(
      currentLookAt,
      targetLookAt.current,
      0.03
    );
    camera.lookAt(newLookAt);
  });

  return (
    <>
      {enableOrbitControls && (
        <OrbitControls
          enableDamping
          dampingFactor={0.05}
          minDistance={5}
          maxDistance={100}
        />
      )}
    </>
  );
}
